import { Button, DatePicker, Flex, Form, Input, Select } from "antd"
import { spacings } from "themes/variables"
import { Modal } from "ui/Modal"
import { useTranslation } from "react-i18next"
import { Title } from "ui/Typography"
import { TextArea } from "ui/Input"
import styles from "./index.module.css"
import { useSelector, useDispatch } from "react-redux"
import { useEffect } from "react"
import dayjs from "dayjs"
import { getCalendarBookings, getCalendarJournalNote, getCalendarTripLocations, saveCalendarJournal } from "store/calendar"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import PropTypes from "prop-types"
import { toaster } from "components/common/Toast"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"

const Journal = ({ open, setOpen, onCancel, selectedDate, startDate, endDate }) => {
  const [form] = Form.useForm()
  const { journalNote, tripLocations } = useSelector((state) => state.calendar)
  const isEditJournal = Boolean(journalNote?.data?.id)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isMobile } = useDevice()

  useEffect(() => {
    dispatch(getCalendarTripLocations())
    dispatch(getCalendarJournalNote(selectedDate))
  }, [selectedDate, dispatch])

  useEffect(() => {
    if (journalNote?.data) {
      const { date, title, body, location } = journalNote.data || {}
      form.setFieldsValue({
        date: selectedDate ? dayjs(selectedDate) : dayjs(date),
        title,
        body,
        location
      })
    } else {
      form.setFieldsValue({
        date: selectedDate ? dayjs(selectedDate) : dayjs()
      })
    }
  }, [journalNote?.data, selectedDate, form])

  const handleFinish = (values) => {
    const formData = {
      ...values,
      date: selectedDate
    }

    dispatch(saveCalendarJournal(journalNote?.data?.id, formData))
      .then((response) => {
        const data = {
          "q[date_gteq]": startDate,
          "q[date_lteq]": endDate
        }
        dispatch(getCalendarBookings(data))
        toaster.success(response?.message)
      })
      .finally(() => {
        setOpen(false)
      })
  }
  const modalContent = (
    <FirstLoading loading={journalNote?.loading}>
      <Flex vertical gap={spacings.XL} className={`p-xl ${isMobile ? "p-0" : styles.journalModalContainer}`}>
        <Title level={5}>{t("calendar.journal.journal_details")}</Title>
        <Form layout="vertical" form={form} onFinish={handleFinish}>
          <Form.Item label={t("calendar.journal.date")} className="fw-medium">
            <Form.Item
              name="date"
              noStyle
              rules={[
                { required: true, message: t("calendar.journal.date_is_required") },
                () => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject()
                    }
                    return Promise.resolve()
                  }
                })
              ]}
            >
              <DatePicker className="w-full" placeholder="Date" format="MM/DD/YYYY" disabled />
            </Form.Item>
          </Form.Item>
          <Form.Item label={t("calendar.journal.title")} className="fw-medium">
            <Form.Item name="title" noStyle>
              <Input placeholder={t("calendar.journal.placeholders.title")} />
            </Form.Item>
          </Form.Item>
          <Form.Item label={t("calendar.journal.body")} className="fw-medium">
            <Form.Item
              name="body"
              noStyle
              rules={[
                { required: true, message: "Body is required" },
                () => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.reject()
                    }

                    return Promise.resolve()
                  }
                })
              ]}
            >
              <TextArea />
            </Form.Item>
          </Form.Item>
          <Form.Item label={t("calendar.journal.location")} className="fw-medium">
            <Form.Item name="location" noStyle>
              <Select placeholder={t("calendar.journal.placeholders.location")} allowClear showSearch optionFilterProp="children">
                {tripLocations?.data?.map((location) => (
                  <Select.Option key={location} value={location}>
                    {location}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form.Item>
          <Flex gap={spacings.S} className="mt-3xl">
            <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onCancel}>
              {t("global.cancel")}
            </Button>
            <Button type="primary" className="py-l px-xl" htmlType="submit">
              {isEditJournal ? t("calendar.journal.save_changes") : t("global.save")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </FirstLoading>
  )

  const journalTitle = isEditJournal
    ? t('calendar.journal.edit_journal')
    : t('calendar.journal.new_journal')

  const modalTitle = journalNote?.loading ? '' : journalTitle

  return isMobile ? (
    <CustomDrawer title={modalTitle} open={open} setOpen={setOpen} onCancel={onCancel} onClose={onCancel}>
      {modalContent}
    </CustomDrawer>
  ) : (
    <Modal centered title={modalTitle} open={open} setOpen={setOpen} onCancel={onCancel} onClose={onCancel}>
      {modalContent}
    </Modal>
  )

}

Journal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  selectedDate: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired
}

export default Journal
